/**
 * ZulassungStatusPage — öffentliche Status-Rückmeldung (Jun-2026 User-Wunsch).
 *
 * Erreichbar via QR-Code/Link aus dem Zulassungs-Übergabebeleg:
 *   /zulassung-status/<token>
 *
 * Der Zulassungsdienst meldet ohne Login den Bearbeitungsstatus zurück:
 * „zugelassen am" + zugeteiltes Kennzeichen (+ optionale Notiz).
 */
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { KeyRound, CheckCircle2, Car, Building2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ZulassungStatusPage() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [registeredDate, setRegisteredDate] = useState("");
    const [plate, setPlate] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await axios.get(`${API}/public/zulassung-status/${token}`);
            setData(r.data);
            if (r.data?.status?.registered) {
                setDone(true);
                setPlate(r.data.status.license_plate || "");
                setRegisteredDate(r.data.status.registered_date || "");
                setNote(r.data.status.note || "");
            }
        } catch (e) {
            setError(e.response?.data?.detail || "Vorgang konnte nicht geladen werden.");
        }
        setLoading(false);
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        if (!registeredDate) { setError("Bitte ein Zulassungsdatum angeben."); return; }
        if (!plate.trim()) { setError("Bitte das zugeteilte Kennzeichen angeben."); return; }
        setBusy(true); setError(null);
        try {
            await axios.post(`${API}/public/zulassung-status/${token}`, {
                registered_date: registeredDate, license_plate: plate.trim(), note: note.trim(),
            });
            setDone(true);
        } catch (e) {
            setError(e.response?.data?.detail || "Rückmeldung fehlgeschlagen.");
        }
        setBusy(false);
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="w-7 h-7 animate-spin text-gray-400" /></div>;
    }
    if (error && !data) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="zulassung-status-error">
                <div className="bg-white border rounded-sm p-6 max-w-md text-center">
                    <p className="text-red-600 font-bold">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-10" data-testid="zulassung-status-page">
            <div className="max-w-lg mx-auto">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-9 h-9 rounded-sm bg-[#111827] flex items-center justify-center"><KeyRound className="w-5 h-5 text-[#FFCC00]" /></div>
                    <div>
                        <h1 className="text-lg font-bold leading-tight">Zulassung — Statusrückmeldung</h1>
                        <p className="text-xs text-gray-500">{data?.provider_name}</p>
                    </div>
                </div>

                <div className="bg-white border rounded-sm p-4 mb-4 text-sm space-y-1.5">
                    <div className="flex items-center gap-2"><Car className="w-4 h-4 text-gray-400" /><span className="font-bold">{data?.vehicle_label}</span></div>
                    {data?.vin && <div className="text-xs text-gray-500">FIN: {data.vin}</div>}
                    {data?.halter && <div className="flex items-center gap-2 text-xs text-gray-500"><Building2 className="w-3.5 h-3.5" /> Halter: {data.halter}</div>}
                </div>

                {done ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-6 text-center" data-testid="zulassung-status-done">
                        <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
                        <p className="font-bold text-emerald-800">Vielen Dank — Status erfasst.</p>
                        <p className="text-sm text-emerald-700 mt-1">Zugelassen am <strong>{registeredDate}</strong> · Kennzeichen <strong>{plate}</strong></p>
                        <p className="text-xs text-gray-500 mt-2">Der Händler wurde automatisch informiert.</p>
                    </div>
                ) : (
                    <div className="bg-white border rounded-sm p-4 space-y-3">
                        <p className="text-sm text-gray-600">Bitte den Bearbeitungsstatus zurückmelden:</p>
                        {error && <p className="text-sm text-red-600" data-testid="zulassung-status-form-error">{error}</p>}
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Zugelassen am *</label>
                            <input type="date" value={registeredDate} onChange={(e) => setRegisteredDate(e.target.value)}
                                className="mt-1 w-full h-10 rounded-sm border border-gray-300 px-2 text-sm" data-testid="zulassung-status-date" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Zugeteiltes Kennzeichen *</label>
                            <input type="text" value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} placeholder="z. B. M-TW 1234"
                                className="mt-1 w-full h-10 rounded-sm border border-gray-300 px-2 text-sm font-mono" data-testid="zulassung-status-plate" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Notiz (optional)</label>
                            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="z. B. Plaketten aufgebracht…"
                                className="mt-1 w-full rounded-sm border border-gray-300 px-2 py-1.5 text-sm resize-none" data-testid="zulassung-status-note" />
                        </div>
                        <button type="button" onClick={submit} disabled={busy}
                            className="w-full h-11 rounded-sm bg-[#111827] hover:bg-black text-white font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                            data-testid="zulassung-status-submit">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Status zurückmelden</>}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
